//@flow
import React, {Component, Fragment} from 'react';
import classNames from 'classnames';
import {FormattedMessage, intlShape} from 'react-intl';
import moment from 'moment';
import icons from '../../../../../../../assets/styles/icons.scss';
import {
  LoadingContentAvatar,
  LoadingContentStripe,
} from '../../../../../../../elements/LoadingContentFigures/LoadingContentFigures';
import {CommaFormat} from '../../../../../../../elements/Price/Price';
import SummaryModal from '../SummaryModal/SummaryModal';
import InternmentPermitModal from '../InternmentPermitModal/InternmentPermitModal';
import RadioButton from './CustomRadioButton/RadioButton';
import PropTypes from 'prop-types';
import sr from 'seedrandom';
import hash from 'object-hash';

import full from '../../../../../../../assets/img/new-order/quote-step/Full.png';
import lite from '../../../../../../../assets/img/new-order/quote-step/Lite.png';

import style from './ForecastBox.scss';
import loadingStripeStyle from '../../../../../../../elements/LoadingContentFigures/LoadingContentFigures.scss';

import type {ForecastCourier, ForecastPricesApiReq, ForecastTier} from '../../../../../QuoteStep/api';

export type ForecastType = 'FULL' | 'LITE';

export type ForecastBoxOption = {
  title: string;
  subtitle?: string;
  description: string;
  price: string;
  noTax?: boolean;
  type: ForecastType;
  subtype: string;
  isTraveler: boolean;
};

type ForecastBoxProps = {
  titleIntl: string;
  titleImage: string;
  descriptionIntl: string;
  selected?: boolean;
  priceLabelIntl: string;
  firstOption: ForecastBoxOption;
  restOptions?: ?ForecastBoxOption[];
  onClickOption: (option: ForecastBoxOption) => void;
  handleClickDetail: (option: ForecastBoxOption) => void;
  handleInternmentPermit: () => void;
  shouldShowHelp?: boolean;
};

type Props = {
  quoteMethodType?: ForecastType;
  quoteFullSubtype?: string;
  quoteLiteSubtype?: string;
  // type: ForecastType;
  handleChangeMethod: ({ type: ForecastType; subtype: string }) => void;
  isLoadingForecast?: boolean;
  forecastClassic?: ForecastTier[];
  forecastCouriers?: ForecastCourier[];
  forecastRequest?: ForecastPricesApiReq;
};

type State = {
  summaryOpen: boolean;
  internmentPermitOpen: boolean;
  modalCourierId?: string;
};

export class FullForecastBox extends Component<Props, State> {
  state = {
    summaryOpen: false,
    internmentPermitOpen: false,
  };

  componentDidUpdate(prevProps: Props) {
    const {quoteMethodType, forecastClassic, handleChangeMethod} = this.props;
    if (prevProps.forecastClassic !== forecastClassic && forecastClassic && !quoteMethodType) {
      const fullOption: ?ForecastTier = this.getFullOption();
      if (fullOption) {
        handleChangeMethod({
          type: 'FULL',
          subtype: fullOption.tier.name,
        });
      }
    }
  }

  getCheaper = (forecastClassic: ForecastTier[]): ForecastTier => {
    return forecastClassic.sort(
      (f: ForecastTier, f2: ForecastTier) => parseFloat(f.total) - parseFloat(f2.total),
    )[0];
  };

  getFullOption = (): ?ForecastTier => {
    const {forecastClassic, forecastRequest, quoteMethodType, quoteFullSubtype} = this.props;

    const selected = quoteMethodType === 'FULL';
    const item = forecastRequest && forecastRequest.items ?
      forecastRequest.items[0] : undefined;

    let fullOption: ?ForecastTier = forecastClassic && forecastClassic
      .find((f: ForecastTier) => {
        let selectedTier;

        if (item) {
          if ([1, 3, 4, 6, 11, 13, 14, 16, 18, 19].includes(item.categoryId)) {
            selectedTier = 'FLEXIBLE';
          } else if ([17].includes(item.categoryId)) {
            selectedTier = 'FAST';
          }
        }

        return f.tier.name === selectedTier;
      });

    // if no fullOption was found (hardcoded rules) it should be the option with the lowest price
    if (!fullOption && forecastClassic) {
      fullOption = forecastClassic.sort((a, b) => parseFloat(a.total) - parseFloat(b.total))[0];
    }

    if (item && forecastClassic) {
      let selectedTier;

      if ([7, 9].includes(item.categoryId)) {
        if (parseFloat(item.weight) >= 3.6) selectedTier = 'FAST';
        else {
          const cheaper = this.getCheaper(forecastClassic);
          selectedTier = cheaper.tier.name;
        }
      } else if ([12, 20].includes(item.categoryId)) {
        const cheaper = this.getCheaper(forecastClassic);
        selectedTier = cheaper.tier.name;
      }

      if (selectedTier) {
        fullOption = forecastClassic
          .find((f: ForecastTier) => f.tier.name === selectedTier);
      }
    }

    if (selected && forecastClassic) {
      fullOption = forecastClassic.find((f: ForecastTier) => f.tier.name === quoteFullSubtype);
    }

    return fullOption;
  };

  render() {
    const {
      quoteMethodType,
      quoteFullSubtype,
      handleChangeMethod,
      forecastClassic,
      forecastRequest,
      isLoadingForecast,
    } = this.props;

    const {summaryOpen, internmentPermitOpen} = this.state;

    const selected = quoteMethodType === 'FULL';

    if (!forecastClassic || isLoadingForecast) {
      return <ForecastBoxLoading/>
    }

    const item = forecastRequest && forecastRequest.items ?
      forecastRequest.items[0] : undefined;

    const fullOption: ?ForecastTier = this.getFullOption();

    const firstOption: $Shape<ForecastBoxOption> = {
      type: 'FULL',
      isTraveler: true,
    };

    if (fullOption) {
      const date = moment().add(fullOption.tier.days, 'days').utc().format('MMMM DD, YYYY', {trim: false});
      firstOption.description = `Recíbelo antes de ${date}`;
      firstOption.price = fullOption.total;
      firstOption.noTax = fullOption.customsDuties === '0.00';
      firstOption.subtype = fullOption.tier.name;

      if (fullOption.tier.name === 'FLEXIBLE') {
        firstOption.title = this.context.intl.formatMessage({id: 'newOrder.quote.classic.deliveryTime.travelers'});
      } else if (fullOption.tier.name === 'FAST') {
        firstOption.title = this.context.intl.formatMessage({id: 'newOrder.quote.classic.deliveryTime.cargo'});
      }
    }

    if (!firstOption || !forecastRequest) {
      return <ForecastBoxLoading/>
    }

    return (
      <Fragment>
        <ForecastBox titleIntl="newOrder.quote.importMethod.classic.title"
                     titleImage={full}
                     descriptionIntl="newOrder.quote.importMethod.classic.description"
                     selected={selected}
                     priceLabelIntl="newOrder.quote.diy.price"
                     firstOption={firstOption}
                     onClickOption={(o) => handleChangeMethod({type: 'FULL', subtype: o.subtype})}
                     handleClickDetail={() => this.setState({summaryOpen: true})}
                     handleInternmentPermit={() => this.setState({internmentPermitOpen: true})}
                     shouldShowHelp={firstOption.subtype === 'FLEXIBLE'}
        />
        <SummaryModal isOpen={summaryOpen}
                      handleClose={() => this.setState({summaryOpen: false})}
                      forecastClassic={forecastClassic}
                      methodType="FULL"
                      fullSubtype={firstOption.subtype}
        />
        <InternmentPermitModal isOpen={internmentPermitOpen}
                               handleClose={() => this.setState({internmentPermitOpen: false})}
        />
      </Fragment>
    );
  }
}

export class LiteForecastBox extends Component<Props, State | { preferredCourierId?: number }> {
  state = {
    summaryOpen: false,
    modalCourierId: undefined,
    internmentPermitOpen: false,
  };

  // TODO: Might come back anytime soon
  componentDidMount() {
    const h = hash((new Date()).getMilliseconds());
    const r = sr(h)();
    console.log('r', r);

    let preferredCourierId: string = '';

    const phoneCouriers = [
      {"courierId": "CR16686519", "name": "Techy Courier"}, 
      {"courierId": "CR08210077", "name": "Storage Courier"},
      {"courierId": "CR09475355", "name": "Rappid Box"}, 
      {"courierId": "CR13528085", "name": "Quick Box"},
      {"courierId": "CR08065879", "name": "VikCourier"}, 
      {"courierId": "CR07961799", "name": "Tráelo BOX"},
      {"courierId": "CR00967701", "name": "FAST Courier"}
    ];

    if (r <= 0.15) preferredCourierId = phoneCouriers[0].courierId;
    else if (r <= 0.25) preferredCourierId = phoneCouriers[1].courierId;
    else if (r <= 0.33) preferredCourierId = phoneCouriers[2].courierId;
    else if (r <= 0.38) preferredCourierId = phoneCouriers[3].courierId;
    else if (r <= 0.61) preferredCourierId = phoneCouriers[4].courierId;
    else if (r <= 0.85) preferredCourierId = phoneCouriers[5].courierId;
    else preferredCourierId = phoneCouriers[6].courierId;

    console.log('Chosen Courier => [%d] %s', r, phoneCouriers.find(c => c.courierId === preferredCourierId).name);

    if (preferredCourierId) {
      this.setState({
        // preferredCourierId: r > 0.5 ? 'CR07961799' : 'CR00967701'
        preferredCourierId,
      });
    }

  }

  // TODO: Might come back anytime soon
  /*componentDidUpdate(prevProps: Props) {
    const {quoteMethodType, quoteLiteSubtype, forecastCouriers, forecastRequest, handleChangeMethod} = this.props;

    if (prevProps.forecastCouriers !== forecastCouriers && forecastCouriers && !quoteMethodType) {
      const item = forecastRequest && forecastRequest.items ? forecastRequest.items[0] : undefined;

      if (item && item.url && item.url.includes('iphone-15')) {
        const filteredForecastCouriers = this.filterForecastCouriers(forecastCouriers);

        const [firstOption, restOptions] = this.getFirstOption(forecastRequest, filteredForecastCouriers, false, quoteLiteSubtype);

        if (firstOption) {
          handleChangeMethod({
            type: 'LITE',
            subtype: firstOption.subtype,
          });
        }
      }
    }
  }*/

  isTraveler = (forecastCourier: ForecastCourier): boolean => {
    return !['CR09043495'].includes(forecastCourier.courier.id);
  };

  buildOption = (forecastCourier: ForecastCourier): ForecastBoxOption => {
    const {forecastRequest} = this.props;

    let description = `Recíbelo en ${forecastCourier.days} días desde la entrega en USA`;

    // THIS IS JUST FOR IPHONE-15
    /*if (forecastRequest && forecastRequest.items.length > 0 && forecastRequest.items[0].categoryId === 3) {
      description = `Recíbelo en ${forecastCourier.days} - 60 días desde la entrega en USA`;
    }*/

    const option: ForecastBoxOption = {
      type: 'LITE',
      price: forecastCourier.total,
      noTax: forecastCourier.customsDuties === '0.00',
      subtype: forecastCourier.courier.id,
      title: forecastCourier.courier.name,
      description,
      isTraveler: false,
    };

    if (this.isTraveler(forecastCourier)) {
      option.subtitle = 'Viajeros';
      option.isTraveler = true;
    }

    return option;
  };

  pickCheaperForecastCourier =
    (forecastCouriers: ForecastCourier[], fn?: (forecastCourier: ForecastCourier) => boolean): [?ForecastBoxOption, ?ForecastBoxOption[]] => {
      let sortedForecastCouriers = forecastCouriers
        .sort((f1: ForecastCourier, f2: ForecastCourier) => parseFloat(f1.total) - parseFloat(f2.total));

      if (fn) {
        sortedForecastCouriers = sortedForecastCouriers.filter(fn);
      }

      const picked = sortedForecastCouriers[0];

      let r = [];

      if (picked) {
        r = sortedForecastCouriers.filter(
          (f: ForecastCourier) => f.courier.id !== picked.courier.id,
        );
        return [
          this.buildOption(picked),
          r.map((f: ForecastCourier) => this.buildOption(f)),
        ];
      }
      return [undefined, undefined];
    };

  filterForecastCouriers = (forecastCouriers: ForecastCourier[]) => {
    return !forecastCouriers ? [] :
      forecastCouriers.filter((fc: ForecastCourier) =>
        !['CR02746321', 'CR14502361'].includes(fc.courier.id));
  };

  getFirstOption = (
    forecastRequest: ForecastPricesApiReq,
    filteredForecastCouriers: ForecastCourier[],
    selected: boolean,
    quoteLiteSubtype: string,
    // returns firstOption and restOptions
  ): [?ForecastBoxOption, ?ForecastBoxOption[]] => {
    let firstOption: ?ForecastBoxOption = undefined;
    let restOptions: ?ForecastBoxOption[] = [];

    const item = forecastRequest && forecastRequest.items ?
      forecastRequest.items[0] : undefined;

    if (item && filteredForecastCouriers) {
      if ([1, 3, 4, 6, 11, 13, 14, 16, 18, 19].includes(item.categoryId)) {
        const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers, (f: ForecastCourier) => this.isTraveler(f));
        firstOption = f;
        restOptions = r;
      } else if ([7, 9].includes(item.categoryId)) {
        if (parseFloat(item.weight) >= 3.6) {
          const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers, (f: ForecastCourier) => !this.isTraveler(f));
          firstOption = f;
          restOptions = r;
        } else {
          const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers);
          firstOption = f;
          restOptions = r;
        }
      } else if ([17].includes(item.categoryId)) {
        const [f, r] = this.pickCheaperForecastCourier(
          filteredForecastCouriers,
          (f: ForecastCourier) => f.courier.id === 'CR09043495',
        );
        firstOption = f;
        restOptions = r;
      } else if ([12, 20].includes(item.categoryId)) {
        const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers);
        firstOption = f;
        restOptions = r;
      }
    }

    // if no firstOption was found (hardcoded rules) it should be the option with the lowest price
    if (!firstOption && filteredForecastCouriers) {
      const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers);
      firstOption = f;
      restOptions = r;
    }

    // TODO: TEMPORARY FOR iphone15
    if (item && item.categoryId === 3) {
      const all = [firstOption, ...restOptions];//.sort((a, b) => a.subtype > b.subtype);

      // if (all.length === 2 && all.every(c => ['CR07961799', 'CR00967701'].includes(c.subtype))) {
      if (this.state.preferredCourierId && firstOption) {
        // console.log('before', all);

        const f = all.find(o => o.subtype === this.state.preferredCourierId);
        const r = all.filter(o => o.subtype !== this.state.preferredCourierId);

        firstOption = {...f};
        restOptions = [...r]

        // console.log('after', [firstOption, ...restOptions]);

        // if (sr(this.state.hashSeed)() > 0.5) {
        //   firstOption = all[1];
        //   restOptions = [all[0]];
        // }else {
        //   firstOption = all[0];
        //   restOptions = [all[1]];
        // }
        // console.log('after', [firstOption.subtype, restOptions[0].subtype]);
      }

    }

    if (selected && filteredForecastCouriers) {
      if (firstOption) {
        const all: ForecastBoxOption[] = [firstOption, ...(restOptions || [])];
        const foundIndex = all.findIndex((option) => option.subtype === quoteLiteSubtype);

        if (foundIndex !== -1) {
          firstOption = all[foundIndex];
          restOptions = all.filter((option) => option.subtype !== quoteLiteSubtype);
        }
      }
    }

    return [firstOption, restOptions];
  };

  render() {
    const {
      quoteMethodType,
      handleChangeMethod,
      forecastCouriers,
      forecastRequest,
      isLoadingForecast,
      quoteLiteSubtype,
    } = this.props;

    const {summaryOpen, modalCourierId, internmentPermitOpen} = this.state;

    // Do not allow: Envíos Condor, Chasquibox
    const filteredForecastCouriers = this.filterForecastCouriers(forecastCouriers);

    if (filteredForecastCouriers.length === 0 || isLoadingForecast) {
      return <ForecastBoxLoading/>
    }

    /*const item = forecastRequest && forecastRequest.items ?
      forecastRequest.items[0] : undefined;*/

    const selected = quoteMethodType === 'LITE';

    const [firstOption, restOptions] = this.getFirstOption(forecastRequest, filteredForecastCouriers, selected, quoteLiteSubtype);

    /*let firstOption: ?ForecastBoxOption = undefined;
    let restOptions: ?ForecastBoxOption[] = [];*/

    /*if (item && filteredForecastCouriers) {
      if ([1, 3, 4, 6, 11, 13, 14, 16, 18, 19].includes(item.categoryId)) {
        const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers, (f: ForecastCourier) => this.isTraveler(f));
        firstOption = f;
        restOptions = r;
      } else if ([7, 9].includes(item.categoryId)) {
        if (parseFloat(item.weight) >= 3.6) {
          const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers, (f: ForecastCourier) => !this.isTraveler(f));
          firstOption = f;
          restOptions = r;
        } else {
          const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers);
          firstOption = f;
          restOptions = r;
        }
      } else if ([17].includes(item.categoryId)) {
        const [f, r] = this.pickCheaperForecastCourier(
          filteredForecastCouriers,
          (f: ForecastCourier) => f.courier.id === 'CR09043495',
        );
        firstOption = f;
        restOptions = r;
      } else if ([12, 20].includes(item.categoryId)) {
        const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers);
        firstOption = f;
        restOptions = r;
      }
    }*/

    /*// if no firstOption was found (hardcoded rules) it should be the option with the lowest price
    if (!firstOption && filteredForecastCouriers) {
      const [f, r] = this.pickCheaperForecastCourier(filteredForecastCouriers);
      firstOption = f;
      restOptions = r;
    }*/

    /*if (selected && filteredForecastCouriers) {
      if (firstOption) {
        const all: ForecastBoxOption[] = [firstOption, ...(restOptions || [])];
        const foundIndex = all.findIndex((option) => option.subtype === quoteLiteSubtype);

        if (foundIndex !== -1) {
          firstOption = all[foundIndex];
          restOptions = all.filter((option) => option.subtype !== quoteLiteSubtype);
        }
      }
    }*/

    if (!firstOption || !forecastRequest) {
      return (
        <Fragment>
          <ForecastBoxLoading/>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <ForecastBox titleIntl="newOrder.quote.importMethod.diy.title"
                     titleImage={lite}
                     descriptionIntl="newOrder.quote.importMethod.diy.description"
                     selected={selected}
                     priceLabelIntl="newOrder.quote.diy.price"
                     firstOption={firstOption}
                     restOptions={restOptions}
                     onClickOption={(o) => handleChangeMethod({type: 'LITE', subtype: o.subtype})}
                     handleClickDetail={(o) => this.setState({summaryOpen: true, modalCourierId: o.subtype})}
                     handleInternmentPermit={() => this.setState({internmentPermitOpen: true})}

        />
        <SummaryModal isOpen={summaryOpen}
                      handleClose={() => this.setState({summaryOpen: false, modalCourierId: undefined})}
                      forecastCouriers={filteredForecastCouriers}
                      methodType="LITE"
                      liteSubtype={modalCourierId}
        />
        <InternmentPermitModal isOpen={internmentPermitOpen}
                               handleClose={() => this.setState({internmentPermitOpen: false})}
                               isTraveler={firstOption.isTraveler}
        />
      </Fragment>
    );
  }

}

export function ForecastBoxLoading() {
  return (
    <div className={classNames(style.forecastWrap, style.loading)}>
      <div className={style.rowWrap}>
        <div className={style.leftWrap}>
          <RadioButton checked={false}
                       name="importMethod"/>
          <div className={style.contentWrap}>
            <div className={style.loadingTitle}>
              <LoadingContentAvatar colorClassName={classNames(loadingStripeStyle.black200, loadingStripeStyle.small)}
                                    width={32}/>
              <LoadingContentStripe colorClassName={loadingStripeStyle.black200}
                                    customClass={style.title}
                                    width={90}/>
            </div>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={200}
                                  margin="0 0 12px 0"/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={200}
                                  margin="0 0 12px 0"/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={200}/>
          </div>
          {/*<div className={style.contentWrap}>
            <h4 className={style.title}>
              <LoadingContentAvatar colorClassName={loadingStripeStyle.black200}/>
              <LoadingContentStripe colorClassName={loadingStripeStyle.black200}/>
            </h4>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}/>
          </div>*/}
        </div>
        <div className={classNames(style.rightWrap, style.first)}>
          <div className={style.infoWrap}>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black200}
                                  width={90}
                                  margin="0 0 12px 0"/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={134}
                                  margin="0 0 12px 0"/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={134}
                                  margin="0 0 12px 0"/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={70}/>
          </div>
          <div className={style.priceWrap}>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black200}
                                  width={62}
                                  margin="0 0 12px auto"/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={75}
                                  margin="0 0 12px auto"/>
            <LoadingContentStripe colorClassName={loadingStripeStyle.black100}
                                  width={46}
                                  margin="0 0 0 auto"/>
          </div>
        </div>
      </div>
    </div>
  );
}

class ForecastBox extends Component<ForecastBoxProps, { restOpen: boolean }> {
  state = {
    restOpen: false,
  };

  render() {
    const {
      titleIntl,
      titleImage,
      descriptionIntl,
      selected = false,
      firstOption,
      restOptions,
      onClickOption,
      handleClickDetail,
      handleInternmentPermit,
      shouldShowHelp = true,
    } = this.props;

    const {restOpen} = this.state;

    const buildLeftWrap = (option: ForecastBoxOption, first: boolean) => (
      <div className={classNames(style.leftWrap, {[style.restWrap]: !first})}>
        <RadioButton checked={first ? selected : false}
                     name="importMethod"/>
        <div className={style.contentWrap}>
          <FormattedMessage id={titleIntl}>{
            txt => (
              <h4 className={style.title}>
                <img src={titleImage} alt="box"/>
                {txt}
              </h4>
            )
          }</FormattedMessage>
          <FormattedMessage id={descriptionIntl}>{
            txt => <p className={style.description}>{txt}</p>
          }</FormattedMessage>
        </div>
      </div>
    );

    const buildRightWrap = (option: ForecastBoxOption, first: boolean) => (
      <div className={classNames(style.rightWrap, {[style.first]: first})}>
        <div className={style.infoWrap}>
          <p className={style.title}>
            {option.title}
            {shouldShowHelp && (
              <button className={style.help}
                      onClick={handleInternmentPermit}>
                <div className={style.tooltipIcon}>?</div>
              </button>
            )}
          </p>
          {option.subtitle && (
            <Fragment>
              <br/>
              <span className={style.subtitle}>{option.subtitle}</span>
            </Fragment>
          )}
          <p className={style.description}>
            {option.description}
          </p>
          {option.noTax && (
            <span className={classNames(style.noTax, {[style.active]: selected && first})}>
              Sin impuestos
          </span>
          )}
        </div>
        <div className={classNames(style.priceWrap, {[style.rest]: !first})}>
          <p className={style.title}>{
            option.type === 'LITE' ? 'Precio de envío' : 'Precio Total'
          }</p>
          <p className={style.price}>$ <CommaFormat price={option.price || ''}/></p>
          {first && (
            <button className={style.detail}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickDetail(option);
                    }}>Ver Detalle
            </button>
          )}
          {!first && (
            <button className={style.detail}>Seleccionar</button>
          )}
        </div>
      </div>
    );

    const buildRowWrap = (option: ForecastBoxOption, first: boolean) => (
      <div key={option.subtype}
           className={classNames(style.rowWrap, {[style.first]: first && restOpen})}
           onClick={() => {
             if (option.subtype) {
               onClickOption(option);
               this.setState({restOpen: false});
             }
           }}>
        {buildLeftWrap(option, first)}
        {buildRightWrap(option, first)}
      </div>
    );

    return (
      <div className={classNames(style.forecastWrap, {[style.active]: selected})}>
        {buildRowWrap(firstOption, true)}
        {(restOpen && restOptions && restOptions.length > 0) && (
          <Fragment>
            {restOptions.map((o) => buildRowWrap(o, false))}
          </Fragment>
        )}
        {(restOptions && restOptions.length > 0) && (
          <button className={style.openButton}
                  onClick={() => this.setState((s) => ({restOpen: !s.restOpen}))}>
            <i className={!restOpen ? icons.iconChevronDownMedium : icons.iconChevronUpMedium}/>
          </button>
        )}
      </div>
    );
  }

}

ForecastBox.propTypes = {
  titleIntl: PropTypes.string.isRequired,
  titleImage: PropTypes.string.isRequired,
  descriptionIntl: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  priceLabelIntl: PropTypes.string.isRequired,
  firstOption: PropTypes.object.isRequired,
  onClickOption: PropTypes.func.isRequired,
  handleClickDetail: PropTypes.func.isRequired,
  handleInternmentPermit: PropTypes.func.isRequired,
}

FullForecastBox.propTypes = LiteForecastBox.propTypes = {
  quoteMethodType: PropTypes.string,
  quoteFullSubtype: PropTypes.string,
  quoteLiteSubtype: PropTypes.string,
  handleChangeMethod: PropTypes.func.isRequired,
  isLoadingForecast: PropTypes.bool,
  forecastClassic: PropTypes.array,
  forecastCouriers: PropTypes.array,
  forecastRequest: PropTypes.object,
};

FullForecastBox.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/ProductStep/components/NewProductForm/QuoteBox/ForecastBox/ForecastBox.jsx